import { useState } from "react";
import { ICON_GROUPS } from "@/utils/consts";
import { NoxusIcon, SIZE_MAP } from "@/components/UI/NoxusIcon";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { NoxusFormLabel } from "../Form/NoxusFormLabel";
import { FileUpload } from "../Form/FileInputField";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../shadcn/tabs";
import { Button } from "../shadcn/button";
import { SpotFile } from "@/types";
import { getSpotFileUrl } from "@/utils/generic";
import { FileTypes } from "@/utils/mimetypes";

export const NoxusIconPicker = ({
  icon = "workflow",
  label,
  handleClick,
  disable = false,
  side = "right",
  align = "start",
  size = "sm",
  disabled = false,
  isPublic = false,
}: {
  icon?: string;
  handleClick?: (icon: string) => void;
  label?: string;
  disable?: boolean;
  side?: "right" | "left" | "bottom" | "top";
  align?: "start" | "end";
  size?: keyof typeof SIZE_MAP;
  disabled?: boolean;
  isPublic?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState<SpotFile[]>([]);

  const handleImageChange = (images: SpotFile[]) => {
    if (disabled) return;
    setImages(images);
    handleClick?.(images?.[0]?.uri || "");
  };

  const handleIconClick = (icon: string) => {
    if (disabled) return;
    handleClick?.(icon);
    setIsOpen(false);
  };

  const isNoxusImage = icon?.startsWith("spot:");

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <div className={disabled ? "pointer-events-none" : undefined}>
      {label && <NoxusFormLabel label={label} />}
      <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
        <PopoverTrigger
          className=" w-fit cursor-pointer h-unset flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (disabled) return;
            setIsOpen(!isOpen);
          }}
          disabled={disabled}
        >
          <NoxusIcon
            size={size}
            icon={icon}
            showBorder={isOpen}
            showBorderOnHover={!disabled}
            isPublic={isPublic}
            showLoader={true}
          />
        </PopoverTrigger>
        <PopoverContent
          side={side}
          align="start"
          className="flex flex-col gap-1rem p-4 overflow-auto shadow-shadow2 rounded-lg border-transparent w-[19.5rem] pointer-events-auto"
          forceMount
        >
          <Tabs defaultValue="icons">
            <TabsList className="mb-4">
              <TabsTrigger
                value="icons"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Icons
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Upload
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="icons"
              className="flex flex-col gap-4"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {ICON_GROUPS.map((iconGroup) => (
                <div
                  key={iconGroup.title}
                  className="flex flex-col items-start gap-2"
                >
                  <div className="grid grid-cols-8 gap-2">
                    {(iconGroup.icons as string[]).map((icon, index) => (
                      <Button
                        key={`icon-${index}`}
                        variant="ghost"
                        className="p-0 h-fit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIconClick(icon);
                        }}
                      >
                        <NoxusIcon icon={icon} size="md" showBorderOnHover />
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="upload">
              <div>
                <NoxusFormLabel label="Upload image" />
                <div className="justify-start items-center w-fit">
                  <FileUpload
                    values={images}
                    setValues={handleImageChange}
                    variant="image"
                    canSelectMultipleFiles={false}
                    allowed_file_types={[FileTypes.IMAGE]}
                    isPublic={isPublic}
                    imageProps={{
                      defaultImage: isNoxusImage
                        ? getSpotFileUrl(icon, false, isPublic)
                        : undefined,
                    }}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </div>
  );
};

interface INoxusIconPickerFieldProps {
  control: Control<any>;
  name: string;
  label?: string;
  rules?: RegisterOptions;
  disable?: boolean;
  side?: "right" | "left" | "bottom" | "top";
  size?: keyof typeof SIZE_MAP;
  disabled?: boolean;
  defaultIcon?: string;
  isPublic?: boolean;
}

export const NoxusIconPickerField = (props: INoxusIconPickerFieldProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      render={({ field: { onChange, value } }) => {
        return (
          <NoxusIconPicker
            label={props.label}
            icon={value || props.defaultIcon}
            handleClick={onChange}
            disable={props.disable}
            size={props.size}
            side={props.side}
            disabled={props.disabled}
            isPublic={props.isPublic}
          />
        );
      }}
    />
  );
};
